import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  PopoverProps,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  advertorialChoices,
  applicationChoices,
  contentTypeChoices,
  deletedContentChoices,
  draftContentChoices,
  flagsChoices,
  photoSalesChoices,
  publicationStatusChoices,
  publishStatusChoices,
  templateContentChoices,
  userContributedChoices,
} from 'components/Inputs/Dropdown/dropdownConsts';
import { NumRangeDropdownInput } from 'components/Inputs/Dropdown/NumRangeDropdownInput';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { TextDateRangeDropdownInput } from 'components/Inputs/Dropdown/TextDateRangeDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import {
  useGetFetchAndFormatAuthors,
  useGetFetchAndFormatPublications,
  useGetFetchAndFormatSections,
  useGetFetchAndFormatWorkflows,
  useGetFetchAndFormatUnlabeledAuthors,
  cacheAuthorChoice,
  getCachedAuthorChoices,
} from 'components/Search/components/SearchAdvance/components/hooks';
import {
  advSearchAnchorOrigin,
  advSearchElevation,
  advSearchTransformOrigin,
  getAdvancedPopoverFooterAreaSx,
  getAdvancedPopoverHeaderAreaSx,
  getAdvancedPopoverInputsAreaSx,
  getAdvancedPopoverRootSx,
  getAdvancedPopoverTitleSx,
  getAdvSearchPopoverPaperSx,
  getAdvSearchThreshold,
  getPopoverGridInputLabelSx,
  resetFiltersButtonSx,
} from 'components/Search/styles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pick } from 'remeda';
import { bloxValues } from 'themes/constants';
import { hasPrintPrivilege } from 'utility/privileges';
import { useSearchModalContext } from '../SearchModal';

type TabPanelProps = {
  active: boolean;
  children: React.ReactNode;
};

// Keep fields in the outer grid
// hide inactive panel without breaking layout
const TabPanel = ({ active, children }: TabPanelProps) => (
  <Box
    role="tabpanel"
    sx={{ display: active ? 'contents' : 'none' }}
  >
    {children}
  </Box>
);

type SearchModalPopoverFiltersEditorialProps = {
  open: boolean;
  setClosed: () => void;
  anchorEl: HTMLElement | null;
  popoverSx?: SxProps<Theme>;
};

/**
 * --------------------------------------------------------
 * Search Modal Advanced Popover - Editorial
 * - Advanced filters for editorial asset search
 * --------------------------------------------------------
 */

export const SearchModalPopoverFiltersEditorial = memo(
  ({ open, setClosed, anchorEl, popoverSx }: SearchModalPopoverFiltersEditorialProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { sessionConfig } = useCosmos();

    // Data fetch hooks for advanced filter dropdowns
    const { fetchAndFormatSections } = useGetFetchAndFormatSections();
    const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
    const { fetchAndFormatUnlabeledAuthors } = useGetFetchAndFormatUnlabeledAuthors();
    const { fetchAndFormatWorkflows } = useGetFetchAndFormatWorkflows();
    const { fetchAndFormatPublications } = useGetFetchAndFormatPublications();

    // check for showing print options
    const hasTotalCMS = hasPrintPrivilege(sessionConfig);

    // Asset Search Modal Context
    const {
      setFilters,
      stagedFilters,
      setStagedFilters,
      keptKeys,
      updateStagedFilterValue,
    } = useSearchModalContext();

    // Tab Panel State
    const [tab, setTab] = useState(0);
    const handleSetTab = useCallback((newTab: number) => {
      setTab(newTab);
    }, []);

    // reset to first tab on close
    const handleClosePopover = useCallback(() => {
      setTab(0);
      setClosed();
    }, [setClosed]);

    // Preserve keys when clearing staged filters
    const handleClearStagedFilters = useCallback(() => {
      const preserved = pick(stagedFilters, keptKeys);
      setStagedFilters(preserved);
    }, [stagedFilters, keptKeys, setStagedFilters]);

    // Apply staged filters to active filters.
    // Note: staged filters should have copied in
    // all filters when popover was opened and
    // staged values should be formatted by the
    // updateStagedFilterValue helper already.
    const handleApplyFilters = useCallback(() => {
      setFilters(stagedFilters);
      handleClosePopover();
    }, [setFilters, stagedFilters, handleClosePopover]);

    // Styles and slots...
    const popoverSlotProps: PopoverProps['slotProps'] = useMemo(() => {
      return {
        root: {
          sx: {
            ...getAdvancedPopoverRootSx(theme),
          },
        },
        paper: {
          sx: {
            ...getAdvSearchPopoverPaperSx(theme),
            ...(typeof popoverSx === 'function' ? popoverSx(theme) : popoverSx),
          },
        },
      };
    }, [theme, popoverSx]);

    const headerSx: SxProps<Theme> = useMemo(() => {
      return {
        gridArea: 'advancedheader',
        ...getAdvancedPopoverHeaderAreaSx(theme),
      };
    }, [theme]);

    const popoverThreshold = useMemo(() => {
      return getAdvSearchThreshold(theme);
    }, [theme]);

    // Translation strings
    const strClearAll = t('blox.actions.clear_all');
    const strApply = t('blox.actions.apply');
    const strAdvancedFilters = t('blox.navigation.assets.advanced_filters');
    const strCloseWithoutApplying = t('blox.general.close_without_applying');

    return (
      <Popover
        data-testid="search-modal-advanced-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        elevation={advSearchElevation}
        marginThreshold={popoverThreshold}
        anchorOrigin={advSearchAnchorOrigin}
        transformOrigin={advSearchTransformOrigin}
        slotProps={popoverSlotProps}
      >
        <Stack
          direction="row"
          sx={[
            {
              justifyContent: 'space-between',
              alignItems: 'center',
            },
            ...(Array.isArray(headerSx) ? headerSx : [headerSx]),
          ]}
        >
          <Typography sx={getAdvancedPopoverTitleSx}>{strAdvancedFilters}</Typography>
          <Tooltip
            title={strCloseWithoutApplying}
            disableInteractive
          >
            <IconButton
              onClick={handleClosePopover}
              size="small"
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {/* Advanced Search Fields Body */}
        <Stack
          data-testid="search-modal-advanced-popover-inputs"
          direction="column"
          sx={[
            {
              justifyContent: 'space-between',
            },
            ...(Array.isArray(getAdvancedPopoverInputsAreaSx)
              ? getAdvancedPopoverInputsAreaSx
              : [getAdvancedPopoverInputsAreaSx]),
          ]}
        >
          <Fragment>
            {/* Full-width tab header */}
            <Box
              className="editorial-popover-tabs"
              sx={{
                gridColumn: '1 / -1',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
                <Tabs
                  value={tab}
                  onChange={(e, v) => handleSetTab(v)}
                  variant="standard"
                  aria-label="Editorial filter tabs"
                  sx={{ p: 0 }}
                >
                  <Tab
                    label={t('blox.navigation.assets.standard_filters')}
                    disableRipple
                    sx={{
                      px: 0,
                      mr: 2,
                      textTransform: 'none',
                      fontSize: bloxValues.smallFontSizeRem,
                    }}
                    data-testid="standard-filters-tab"
                  />
                  <Tab
                    label={t('blox.navigation.assets.more_filters')}
                    disableRipple
                    sx={{
                      px: 0,
                      textTransform: 'none',
                      fontSize: bloxValues.smallFontSizeRem,
                    }}
                    data-testid="more-filters-tab"
                  />
                </Tabs>
              </Box>
            </Box>

            {/* Standard Filters */}
            <TabPanel active={tab === 0}>
              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.type.label')}
              </Typography>
              <SelectDropdownInput
                staticChoices={contentTypeChoices}
                inputLabel={t('blox.editorial.asset.type.label')}
                value={stagedFilters?.type}
                updateValue={(event, value) => updateStagedFilterValue('type', value)}
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.start_time.label')}
              </Typography>
              <TextDateRangeDropdownInput
                inputLabel={t('blox.editorial.asset.start_time.label')}
                value={[
                  stagedFilters['start_time[min]'] || '',
                  stagedFilters['start_time[max]'] || '',
                ]}
                updateValue={(event, value) => {
                  updateStagedFilterValue('start_time[min]', value[0]);
                  updateStagedFilterValue('start_time[max]', value[1]);
                }}
                applyChangesOnPopoverClose
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                useCalendarIconInSelect={true}
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.metadata.group.print_slug.label')}
              </Typography>
              <TextDropdownInput
                inputLabel={t('blox.editorial.metadata.group.print_slug.label')}
                value={stagedFilters.print_slug}
                updateValue={(event, value) =>
                  updateStagedFilterValue('print_slug', value)
                }
                applyChangesOnPopoverClose
                popoverMatchesFormControlWidth
                filterPlaceholderText="Find exact slug..."
                formControlVariant="outlined"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.authors.label_one')}
              </Typography>
              <SelectDropdownInput
                inputLabel={t('blox.editorial.asset.authors.label_one')}
                value={
                  Array.isArray(stagedFilters.author)
                    ? stagedFilters.author[0] || ''
                    : stagedFilters.author || ''
                }
                updateValue={(event, value) => updateStagedFilterValue('author', value)}
                fetchDataFn={fetchAndFormatAuthors}
                fetchUnlabeledItemsFn={fetchAndFormatUnlabeledAuthors}
                getCachedItemsFn={getCachedAuthorChoices}
                onChoiceSelected={cacheAuthorChoice}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
                isDataPrivate={true}
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.sections.label')}
              </Typography>
              <SelectDropdownInput
                inputLabel={t('blox.editorial.asset.sections.label')}
                value={stagedFilters.section}
                updateValue={(event, values) =>
                  updateStagedFilterValue('section', values)
                }
                fetchDataFn={fetchAndFormatSections}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.metadata.group.workflow.label')}
              </Typography>
              <SelectDropdownInput
                inputLabel={t('blox.editorial.metadata.group.workflow.label')}
                value={stagedFilters.workflow}
                updateValue={(event, value) => updateStagedFilterValue('workflow', value)}
                fetchDataFn={fetchAndFormatWorkflows}
                showFilter
                showFooter={false}
                showChipBox={true}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.properties.no_publish.label_alt')}
              </Typography>
              <SelectDropdownInput
                staticChoices={publishStatusChoices}
                inputLabel={t('blox.editorial.asset.properties.no_publish.label_alt')}
                value={stagedFilters.publish_status}
                updateValue={(event, value) =>
                  updateStagedFilterValue('publish_status', value)
                }
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.flags.label')}
              </Typography>
              <SelectDropdownInput
                staticChoices={flagsChoices}
                inputLabel={t('blox.editorial.asset.flags.label')}
                value={stagedFilters.flag}
                updateValue={(event, value) => updateStagedFilterValue('flag', value)}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />
            </TabPanel>

            {/* More Filters */}
            <TabPanel active={tab === 1}>
              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.draft.label')}
              </Typography>
              <SelectDropdownInput
                staticChoices={draftContentChoices}
                inputLabel={t('blox.editorial.asset.draft.label')}
                value={stagedFilters.draft}
                updateValue={(event, value) => updateStagedFilterValue('draft', value)}
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.general.deleted')}
              </Typography>
              <SelectDropdownInput
                staticChoices={deletedContentChoices}
                inputLabel={t('blox.general.deleted')}
                value={stagedFilters.deleted}
                updateValue={(event, value) => updateStagedFilterValue('deleted', value)}
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.general.template')}
              </Typography>
              <SelectDropdownInput
                staticChoices={templateContentChoices}
                inputLabel={t('blox.general.template')}
                value={stagedFilters.template}
                updateValue={(event, value) => updateStagedFilterValue('template', value)}
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.general.value_added_content')}
              </Typography>
              <SelectDropdownInput
                staticChoices={advertorialChoices}
                inputLabel={t('blox.general.value_added_content')}
                value={stagedFilters.advertorial}
                updateValue={(event, value) =>
                  updateStagedFilterValue('advertorial', value)
                }
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.general.contributed')}
              </Typography>
              <SelectDropdownInput
                staticChoices={userContributedChoices}
                inputLabel={t('blox.general.contributed')}
                value={stagedFilters.contributed}
                updateValue={(event, value) =>
                  updateStagedFilterValue('contributed', value)
                }
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.properties.app.label')}
              </Typography>
              <SelectDropdownInput
                staticChoices={applicationChoices}
                inputLabel={t('blox.editorial.asset.properties.app.label')}
                value={stagedFilters.app}
                updateValue={(event, value) => updateStagedFilterValue('app', value)}
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                renderValueDisplay="chips"
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                multiple
              />

              <Typography sx={getPopoverGridInputLabelSx}>
                {t('blox.editorial.asset.properties.photo_sales.label')}
              </Typography>
              <SelectDropdownInput
                staticChoices={photoSalesChoices}
                inputLabel={t('blox.editorial.asset.properties.photo_sales.label')}
                value={stagedFilters.photo_sales}
                updateValue={(event, value) =>
                  updateStagedFilterValue('photo_sales', value)
                }
                showFilter={false}
                showFooter={false}
                showChipBox={false}
                popoverMatchesFormControlWidth
                formControlVariant="outlined"
                inputPlaceholderText="stringify-options"
                renderValueDisplay="chips"
              />

              {hasTotalCMS && (
                <Fragment>
                  <Typography sx={getPopoverGridInputLabelSx}>
                    {t('blox.editorial.metadata.group.print_ready.label')}
                  </Typography>
                  <SelectDropdownInput
                    staticChoices={photoSalesChoices}
                    inputLabel={t('blox.editorial.metadata.group.print_ready.label')}
                    value={stagedFilters.print_ready}
                    updateValue={(event, value) =>
                      updateStagedFilterValue('print_ready', value)
                    }
                    showFilter={false}
                    showFooter={false}
                    showChipBox={false}
                    popoverMatchesFormControlWidth
                    formControlVariant="outlined"
                    inputPlaceholderText="stringify-options"
                    renderValueDisplay="chips"
                  />

                  <Typography sx={getPopoverGridInputLabelSx}>
                    {t('blox.editorial.metadata.group.print_depth.label')}
                  </Typography>
                  <NumRangeDropdownInput
                    valueMode="normal"
                    inputLabel={t('blox.editorial.metadata.group.print_depth.label')}
                    inputPlaceholderText={t(
                      'blox.editorial.metadata.group.print_depth.label_alt',
                    )}
                    value={[
                      stagedFilters['print_depth[min]'] || '',
                      stagedFilters['print_depth[max]'] || '',
                    ]}
                    updateValue={(event, value) => {
                      updateStagedFilterValue('print_depth[min]', value[0]);
                      updateStagedFilterValue('print_depth[max]', value[1]);
                    }}
                    applyChangesOnPopoverClose={true}
                    renderValueDisplay="chips"
                    formControlVariant="outlined"
                    popoverMatchesFormControlWidth={true}
                  />

                  <Typography sx={getPopoverGridInputLabelSx}>
                    {t('blox.editorial.metadata.group.print_publication.label')}
                  </Typography>
                  <SelectDropdownInput
                    inputLabel={t(
                      'blox.editorial.metadata.group.print_publication.label',
                    )}
                    value={stagedFilters.print_publication}
                    fetchDataFn={fetchAndFormatPublications}
                    updateValue={(event, value) => {
                      if (value === null || value === '') {
                        // Clear all options that require print_publication to be set
                        updateStagedFilterValue('print_publication_date[min]', '');
                        updateStagedFilterValue('print_publication_date[max]', '');
                        updateStagedFilterValue('print_publication_status', '');
                      }
                      updateStagedFilterValue('print_publication', value);
                    }}
                    showFilter={false}
                    showFooter={false}
                    showChipBox={false}
                    popoverMatchesFormControlWidth={true}
                    formControlVariant="outlined"
                    inputPlaceholderText="stringify-options"
                    renderValueDisplay="chips"
                  />

                  <Typography sx={getPopoverGridInputLabelSx}>
                    {t(
                      'blox.editorial.metadata.group.print_publication_date_range.label',
                    )}
                  </Typography>
                  <TextDateRangeDropdownInput
                    inputLabel={
                      stagedFilters.print_publication
                        ? t(
                            'blox.editorial.metadata.group.print_publication_date_range.label',
                          )
                        : t(
                            'blox.editorial.metadata.group.print_publication_date_range.help',
                          )
                    }
                    value={[
                      stagedFilters['print_publication_date[min]'] || '',
                      stagedFilters['print_publication_date[max]'] || '',
                    ]}
                    updateValue={(event, value) => {
                      updateStagedFilterValue('print_publication_date[min]', value[0]);
                      updateStagedFilterValue('print_publication_date[max]', value[1]);
                    }}
                    disabled={Boolean(!stagedFilters.print_publication)}
                    applyChangesOnPopoverClose={true}
                    popoverMatchesFormControlWidth={true}
                    renderValueDisplay="chips"
                    formControlVariant="outlined"
                    outputFormat="YYYY-MM-DD"
                    displayFormat="pretty"
                    useCalendarIconInSelect={true}
                  />

                  <Typography sx={getPopoverGridInputLabelSx}>
                    {t('blox.editorial.metadata.group.print_publication_status.label')}
                  </Typography>
                  <SelectDropdownInput
                    staticChoices={publicationStatusChoices}
                    inputLabel={
                      stagedFilters.print_publication
                        ? t(
                            'blox.editorial.metadata.group.print_publication_status.label',
                          )
                        : t('blox.editorial.metadata.group.print_publication_status.help')
                    }
                    value={stagedFilters.print_publication_status}
                    updateValue={(event, value) =>
                      updateStagedFilterValue('print_publication_status', value)
                    }
                    disabled={Boolean(!stagedFilters.print_publication)}
                    showFilter={false}
                    showFooter={false}
                    showChipBox={false}
                    popoverMatchesFormControlWidth={true}
                    formControlVariant="outlined"
                    renderValueDisplay="chips"
                  />
                </Fragment>
              )}
            </TabPanel>
          </Fragment>
        </Stack>
        <Stack
          data-testid="search-modal-advanced-popover-footer"
          direction="row"
          sx={[
            {
              justifyContent: 'flex-end',
              alignItems: 'center',
            },
            ...(Array.isArray(getAdvancedPopoverFooterAreaSx)
              ? getAdvancedPopoverFooterAreaSx
              : [getAdvancedPopoverFooterAreaSx]),
          ]}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearStagedFilters}
            color="info"
            disableElevation
            sx={{ width: '100px' }}
          >
            {strClearAll}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleApplyFilters}
            disableElevation
            sx={{ width: '100px' }}
          >
            {strApply}
          </Button>
        </Stack>
      </Popover>
    );
  },
);
SearchModalPopoverFiltersEditorial.displayName = 'SearchModalPopoverFiltersEditorial';

/**
 * --------------------------------------------------------
 * Search Modal Active Filters Bar - Editorial
 * - Active filters row to display below search input
 * --------------------------------------------------------
 */

export const SearchModalActiveFiltersBarEditorial = memo(() => {
  // Data hooks for advanced filter dropdowns
  const { fetchAndFormatSections } = useGetFetchAndFormatSections();
  const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
  const { fetchAndFormatUnlabeledAuthors } = useGetFetchAndFormatUnlabeledAuthors();
  const { fetchAndFormatWorkflows } = useGetFetchAndFormatWorkflows();
  const { fetchAndFormatPublications } = useGetFetchAndFormatPublications();

  const { t } = useTranslation();
  const {
    updateFilterValue,
    filters,
    showAdvFilters,
    advFilterCount,
    clearFilters,
    keptKeys,
  } = useSearchModalContext();

  const showResetButton = Boolean(advFilterCount > 0);

  // Reset all advanced filters
  const handleResetFilters = useCallback(() => {
    clearFilters(keptKeys);
  }, [clearFilters, keptKeys]);

  // Translation strings
  const strResetFilters = t('blox.actions.reset_filters');

  return (
    <Fragment>
      <SelectDropdownInput
        staticChoices={contentTypeChoices}
        inputLabel={t('blox.editorial.asset.type.label')}
        value={filters?.type || []}
        updateValue={(event, value) => updateFilterValue('type', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showType}
        multiple
      />

      <TextDateRangeDropdownInput
        inputLabel={t('blox.editorial.asset.start_time.label')}
        value={[filters?.['start_time[min]'] || '', filters?.['start_time[max]'] || '']}
        updateValue={(event, value) => {
          updateFilterValue('start_time[min]', value[0]);
          updateFilterValue('start_time[max]', value[1]);
        }}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showStartTime}
      />

      <TextDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_slug.label')}
        value={filters?.print_slug || ''}
        updateValue={(event, value) => updateFilterValue('print_slug', value)}
        debounceUpdate={300}
        hidden={!showAdvFilters?.showPrintSlug}
        filterPlaceholderText={t('blox.editorial.metadata.group.print_slug.help')}
        applyChangesOnPopoverClose
      />

      <SelectDropdownInput
        staticChoices={draftContentChoices}
        inputLabel={t('blox.editorial.asset.draft.label')}
        value={filters?.draft || ''}
        updateValue={(event, value) => updateFilterValue('draft', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showDraft}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.asset.sections.label')}
        value={filters?.section || []}
        updateValue={(event, values) => updateFilterValue('section', values)}
        fetchDataFn={fetchAndFormatSections}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showSection}
        multiple
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.metadata.group.workflow.label')}
        value={filters?.workflow || []}
        updateValue={(event, value) => updateFilterValue('workflow', value)}
        fetchDataFn={fetchAndFormatWorkflows}
        infiniteScroll={false}
        showFilter
        showFooter={false}
        showChipBox={true}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showWorkflow}
        multiple
      />

      <SelectDropdownInput
        staticChoices={publishStatusChoices}
        inputLabel={t('blox.editorial.asset.properties.no_publish.label_alt')}
        value={filters?.publish_status || []}
        updateValue={(event, value) => updateFilterValue('publish_status', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showPublishStatus}
        multiple
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.asset.authors.label_one')}
        value={
          Array.isArray(filters?.author)
            ? filters?.author[0] || ''
            : filters?.author || ''
        }
        updateValue={(event, values) => updateFilterValue('author', values)}
        fetchDataFn={fetchAndFormatAuthors}
        fetchUnlabeledItemsFn={fetchAndFormatUnlabeledAuthors}
        getCachedItemsFn={getCachedAuthorChoices}
        onChoiceSelected={cacheAuthorChoice}
        hidden={!showAdvFilters?.showAuthor}
        isDataPrivate={true}
      />

      <SelectDropdownInput
        staticChoices={deletedContentChoices}
        inputLabel={t('blox.general.deleted')}
        value={filters?.deleted || ''}
        updateValue={(event, value) => updateFilterValue('deleted', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showDeleted}
      />

      <SelectDropdownInput
        staticChoices={templateContentChoices}
        inputLabel={t('blox.general.template')}
        value={filters?.template || ''}
        updateValue={(event, value) => updateFilterValue('template', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showTemplate}
      />

      <SelectDropdownInput
        staticChoices={flagsChoices}
        inputLabel={t('blox.editorial.asset.flags.label')}
        value={filters?.flag || []}
        updateValue={(event, value) => updateFilterValue('flag', value)}
        infiniteScroll={false}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showFlag}
        multiple
      />

      <SelectDropdownInput
        staticChoices={advertorialChoices}
        inputLabel={t('blox.general.value_added_content')}
        value={filters?.advertorial || ''}
        updateValue={(event, value) => updateFilterValue('advertorial', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showAdvertorial}
      />

      <SelectDropdownInput
        staticChoices={userContributedChoices}
        inputLabel={t('blox.general.contributed')}
        value={filters?.contributed || ''}
        updateValue={(event, value) => updateFilterValue('contributed', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showContributed}
      />

      <SelectDropdownInput
        staticChoices={applicationChoices}
        inputLabel={t('blox.editorial.asset.properties.app.label')}
        value={filters?.app || []}
        updateValue={(event, value) => updateFilterValue('app', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        applyChangesOnPopoverClose
        hidden={!showAdvFilters?.showApp}
        multiple
      />

      <SelectDropdownInput
        staticChoices={photoSalesChoices}
        inputLabel={t('blox.editorial.asset.properties.photo_sales.label')}
        value={filters?.photo_sales || ''}
        updateValue={(event, value) => updateFilterValue('photo_sales', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showPhotoSales}
      />

      <SelectDropdownInput
        staticChoices={photoSalesChoices}
        inputLabel={t('blox.editorial.metadata.group.print_ready.label')}
        value={filters?.print_ready || ''}
        updateValue={(event, value) => updateFilterValue('print_ready', value)}
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showPrintReady}
      />

      <NumRangeDropdownInput
        valueMode="normal"
        inputLabel={t('blox.editorial.metadata.group.print_depth.label')}
        inputPlaceholderText="Print Depth Range"
        value={[filters?.['print_depth[min]'] || '', filters?.['print_depth[max]'] || '']}
        updateValue={(event, value) => {
          updateFilterValue('print_depth[min]', value[0]);
          updateFilterValue('print_depth[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={!showAdvFilters?.showPrintDepthRange}
      />

      <SelectDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_publication.label')}
        value={filters?.print_publication || ''}
        updateValue={(event, values) => updateFilterValue('print_publication', values)}
        fetchDataFn={fetchAndFormatPublications}
        hidden={!showAdvFilters?.showPrintPublication}
        isDataPrivate={true}
      />

      <TextDateRangeDropdownInput
        inputLabel={t('blox.editorial.metadata.group.print_publication_date_range.label')}
        inputPlaceholderText={t(
          'blox.editorial.metadata.group.print_publication_date_range.label',
        )}
        value={[
          filters?.['print_publication_date[min]'] || '',
          filters?.['print_publication_date[max]'] || '',
        ]}
        updateValue={(event, value) => {
          updateFilterValue('print_publication_date[min]', value[0]);
          updateFilterValue('print_publication_date[max]', value[1]);
        }}
        applyChangesOnPopoverClose={true}
        hidden={!showAdvFilters?.showPrintPublicationDateRange}
        outputFormat="YYYY-MM-DD"
        displayFormat="pretty"
      />

      <SelectDropdownInput
        staticChoices={publicationStatusChoices}
        inputLabel={t('blox.editorial.metadata.group.print_publication_status.label')}
        value={filters?.print_publication_status || ''}
        updateValue={(event, value) =>
          updateFilterValue('print_publication_status', value)
        }
        infiniteScroll={false}
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        hidden={!showAdvFilters?.showPrintPublicationStatus}
      />

      {/** Reset Filters Button */}
      {showResetButton && (
        <Fragment>
          <Divider
            orientation="vertical"
            flexItem
          />
          <Button
            data-testid="search-modal-reset-filters-button"
            onClick={handleResetFilters}
            variant="text"
            size="small"
            color="info"
            sx={resetFiltersButtonSx}
          >
            {strResetFilters}
          </Button>
        </Fragment>
      )}
    </Fragment>
  );
});
SearchModalActiveFiltersBarEditorial.displayName = 'SearchModalActiveFiltersBarEditorial';
